import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SysNotifyService, TaskService, VendorsService, UserService, AuthService, RequestService, DistributionGroupService, TenantService, WebSocketService, SoundService, DASHBOARD_POLLING_WS_DISCONNECTED_MS, DASHBOARD_POLLING_WS_CONNECTED_SAFETY_MS } from '@org/shared-services';
import { Observable, BehaviorSubject, of, interval, Subscription, throwError, EMPTY, forkJoin, Subject, timer } from 'rxjs';
import { TruncatePipe } from '@org/utilities';
import { VendorTask, Task, User, Role, Sublet, SubletVendorResponse, SubletVendorAssignment, ExternalVendorTask, ExternalVendorTaskResponse, Media } from '@org/interfaces';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { VendorAssociateComponent, TypeAheadComponent, ConfirmationModalTWComponent, ImageViewerComponent } from '@org/components';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { map, takeWhile, catchError, switchMap, tap, finalize, takeUntil, filter, delay } from 'rxjs/operators';
import * as moment from 'moment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ImageViewerService } from '@org/shared-services';

@Component({
  selector: 'app-vendor-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    TruncatePipe,
    VendorAssociateComponent,
    ReactiveFormsModule,
    FormsModule,
    ConfirmationModalTWComponent,
    TypeAheadComponent,
    ImageViewerComponent,
  ],
  templateUrl: './vendor-dashboard.component.html',
  animations: [
    trigger('slideInOut', [
      state('in', style({
        transform: 'translateX(0)'
      })),
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in')
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VendorDashboardComponent implements OnInit, OnDestroy {
  isLoading = false;
  vendorTasks$: Observable<ExternalVendorTask[]>;
  vendors$!: Observable<boolean>;
  selectedTask: ExternalVendorTask | null = null;
  private vendorTasksSubject = new BehaviorSubject<ExternalVendorTask[]>([]);
  tasksWithSublets: Task[] = [];
  isFocused = false;
  flagTextMap: { [key: string]: string } = {
    'red': 'High Priority',
    'yellow': 'Medium Priority',
    'green': 'Low Priority'
  };
  lastUpdateTime: Date = new Date();
  userCache: { [key: string]: User } = {};
  unreadCount = 0;
  
  // Pagination properties
  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 1;

  // Search and filter properties
  searchTerm = '';
  urgentFilter = false;
  unreadFilter = false;
  viewMode: 'list' | 'grid' = 'list';

  // Task metrics
  taskMetrics = {
    active: 0,
    overdue: 0
  };

  private readTasks = new Set<string>();

  // Polling properties
  private pollingInterval = 30000; // 30 seconds
  private pollingSubscription: Subscription | null = null;
  lastPollTime: Date | null = null;
  nextPollTime: Date | null = null;

  private previousActiveTasksCount = 0;

  // Add new properties for WebSocket handling
  private isWebSocketConnected = false;
  private lastWebSocketUpdate: Date | null = null;
  private readonly POLLING_INTERVAL_WS_DISCONNECTED = DASHBOARD_POLLING_WS_DISCONNECTED_MS;
  private readonly POLLING_INTERVAL_WS_CONNECTED = DASHBOARD_POLLING_WS_CONNECTED_SAFETY_MS;

  private destroy$ = new Subject<void>();
  private wsConnectionTimeout$ = new Subject<void>();
  private readonly WS_CONNECTION_TIMEOUT = 20000; // 20 seconds

  selectedImage: { url: string; caption: string } | null = null;
  @ViewChild(ImageViewerComponent) imageViewer!: ImageViewerComponent;
  selectedImageIndex: number | null = null;

  // Confirmation modal state
  isCompleteConfirmOpen = false;
  taskToComplete: ExternalVendorTask | null = null;

  constructor(
    private vendorsService: VendorsService,
    private taskService: TaskService,
    private requestService: RequestService,
    private userService: UserService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private sysNotifyService: SysNotifyService,
    private fb: FormBuilder,
    private distroGroupService: DistributionGroupService,
    private tenantService: TenantService,
    private ws: WebSocketService,
    private snackBar: MatSnackBar,
    private router: Router,
    private imageViewerService: ImageViewerService,
    private soundService: SoundService
  ) {
    this.vendorTasks$ = this.vendorTasksSubject.asObservable();
    this.vendors$ = this.vendorsService.vendors$;

    // Subscribe to WebSocket connection status with timeout
    this.ws.connectionStatus$.pipe(
      takeUntil(this.destroy$),
      filter((isConnected: boolean) => isConnected !== undefined)
    ).subscribe((isConnected: boolean) => {
      this.isWebSocketConnected = isConnected;

      if (isConnected) {
        this.setupWebSocketListeners();
        // Cancel any pending polling start
        this.wsConnectionTimeout$.next();
      } else {
        // Start a timer to initiate polling if WebSocket doesn't connect
        timer(this.WS_CONNECTION_TIMEOUT).pipe(
          takeUntil(this.destroy$),
          takeUntil(this.wsConnectionTimeout$)
        ).subscribe(() => {
          if (!this.isWebSocketConnected) {
            this.updatePollingInterval();
            this.startPolling();
          }
        });
      }
    });
  }

  ngOnInit() {
    // Initial data load
    this.loadVendorTasks().pipe(
      takeUntil(this.destroy$),
      delay(0) // Ensure this runs after component initialization
    ).subscribe({
      next: () => {
        this.loadTasksWithSublets().pipe(
          takeUntil(this.destroy$)
        ).subscribe();
        this.lastUpdateTime = new Date();
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error in initial data load:', error);
        this.cdr.markForCheck();
      }
    });

    // Connect WebSocket
    this.ws.connect();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.wsConnectionTimeout$.next();
    this.wsConnectionTimeout$.complete();
    this.stopPolling();
    const socket = this.ws.getIo();
    if (socket) {
      socket.off('vendorUpdate');
      socket.off('taskUpdate');
      socket.off('sendUpdateMessageToRole');
    }
  }

  private startPolling() {
    if (this.pollingSubscription) {
      this.stopPolling();
    }

    this.lastPollTime = new Date();
    this.updateNextPollTime();

    this.pollingSubscription = interval(this.pollingInterval).pipe(
      takeUntil(this.destroy$),
      takeWhile(() => !this.isWebSocketConnected || 
        !this.lastWebSocketUpdate || 
        (new Date().getTime() - this.lastWebSocketUpdate.getTime()) > this.POLLING_INTERVAL_WS_CONNECTED)
    ).subscribe({
      next: () => {
        if (!this.isWebSocketConnected) {
          this.refreshData().pipe(
            takeUntil(this.destroy$)
          ).subscribe({
            error: (error) => {
              console.error('Error during polling refresh:', error);
              this.stopPolling();
              this.sysNotifyService.showError('Error refreshing data. Please refresh the page.');
            }
          });
        }
      },
      error: (error) => {
        console.error('Polling subscription error:', error);
        this.stopPolling();
        this.sysNotifyService.showError('Connection error. Please refresh the page.');
      }
    });
  }

  private stopPolling() {
    try {
      if (this.pollingSubscription) {
        this.pollingSubscription.unsubscribe();
        this.pollingSubscription = null;
      }
      this.nextPollTime = null;
    } catch (error) {
      console.error('Error stopping polling:', error);
    }
  }

  refreshData() {
    if (this.isLoading) {
      console.warn('Refresh skipped - previous request still in progress');
      return EMPTY;
    }

    this.isLoading = true;
    return forkJoin({
      vendorTasks: this.loadVendorTasks(),
      sublets: this.loadTasksWithSublets()
    }).pipe(
      finalize(() => {
        this.isLoading = false;
        this.lastPollTime = new Date();
        this.updateNextPollTime();
        this.cdr.detectChanges();
      }),
      catchError(error => {
        console.error('Error during data refresh:', error);
        this.sysNotifyService.showError('Error refreshing data');
        return EMPTY;
      })
    );
  }

  private updateNextPollTime() {
    if (this.lastPollTime) {
      this.nextPollTime = new Date(this.lastPollTime.getTime() + this.pollingInterval);
    }
  }

  getTimeUntilNextPoll(): string {
    if (!this.nextPollTime) return '';
    const now = new Date();
    const diff = this.nextPollTime.getTime() - now.getTime();
    const seconds = Math.floor(diff / 1000);
    return `${seconds}s`;
  }

  loadVendorTasks() {
    return this.vendorsService.getVendorTasksByVendorId().pipe(
      map((response: ExternalVendorTaskResponse) => {
        const tasks = response.vendors;
        const sortedTasks = tasks.sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA;
        });
        
        const currentActiveTasksCount = sortedTasks.filter(task => !task.taskCompleted).length;
        
        if (currentActiveTasksCount > this.previousActiveTasksCount) {
          this.playNotificationSound();
        }
        
        this.previousActiveTasksCount = currentActiveTasksCount;
        this.vendorTasksSubject.next(sortedTasks);
        this.totalPages = Math.ceil(sortedTasks.length / this.itemsPerPage);
        this.updateTaskMetrics(sortedTasks);
        this.unreadCount = response.unreadCount;
        this.cdr.detectChanges();
        return response;
      }),
      catchError(error => {
        console.error('Error loading vendor tasks:', error);
        this.vendorTasksSubject.next([]);
        this.sysNotifyService.showError('Error loading tasks');
        return throwError(() => error);
      })
    );
  }

  loadTasksWithSublets() {
    return this.vendorsService.getTasksWithSublets().pipe(
      map((tasks: Task[]) => {
        this.tasksWithSublets = tasks;
        this.cdr.detectChanges();
        return tasks;
      }),
      catchError(error => {
        console.error('Error loading requests with sublets:', error);
        this.sysNotifyService.showError('Error loading sublets');
        return throwError(() => error);
      })
    );
  }

  // Search and filter methods
  onSearchInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
    this.filterTasks();
  }

  filterByUrgent() {
    this.urgentFilter = !this.urgentFilter;
    this.filterTasks();
  }

  filterByUnread() {
    this.unreadFilter = !this.unreadFilter;
    this.filterTasks();
  }

  toggleViewMode(mode: 'list' | 'grid') {
    this.viewMode = mode;
    this.cdr.detectChanges();
  }

  private filterTasks() {
    this.vendorTasks$.pipe(
      map(tasks => {
        return tasks.filter(task => {
          let matches = true;
          
          if (this.searchTerm) {
            const searchLower = this.searchTerm.toLowerCase();
            matches = matches && (
              task.requestDetails?.toLowerCase().includes(searchLower) ||
              task.roNumber?.toLowerCase().includes(searchLower) ||
              task.servicesOffered?.toLowerCase().includes(searchLower)
            );
          }

          if (this.urgentFilter) {
            matches = matches && task.task?.flag === 'red';
          }

          if (this.unreadFilter) {
            matches = matches && this.isTaskUnread(task);
          }

          return matches;
        });
      })
    ).subscribe(filteredTasks => {
      this.vendorTasksSubject.next(filteredTasks);
      this.totalPages = Math.ceil(filteredTasks.length / this.itemsPerPage);
      this.currentPage = 1;
      this.cdr.detectChanges();
    });
  }

  private updateTaskMetrics(tasks: ExternalVendorTask[]) {
    this.taskMetrics = {
      active: tasks.filter(task => !task.taskCompleted).length,
      overdue: tasks.filter(task => {
        if (!task.dueDate || task.taskCompleted) return false;
        return new Date(task.dueDate) < new Date();
      }).length
    };
  }

  // Pagination methods
  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.cdr.detectChanges();
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.cdr.detectChanges();
    }
  }

  getAssignedByTenant(task: ExternalVendorTask): string {
    return task.vendorWorkCreatorTenant?.name || 'Unknown Tenant';
  }

  getAssignedByContact(task: ExternalVendorTask): string {
    return task.vendorWorkCreator?.name || 'Unknown Contact';
  }

  getServiceOffered(task: ExternalVendorTask): string {
    return task.servicesOffered || 'No Service Specified';
  }

  getTimeSinceCreation(task: ExternalVendorTask): string {
    if (!task.createdAt) return 'Unknown Time';
    return moment(task.createdAt).fromNow();
  }

  getTaskTitle(task: ExternalVendorTask): string {
    return task.requestDetails || 'Untitled Task';
  }

  getTaskDescription(task: ExternalVendorTask): string {
    return task.requestDetails || 'No description available';
  }

  getTaskComments(task: ExternalVendorTask): any[] {
    return Array.isArray(task.task?.comments) ? task.task.comments : [];
  }

  getTaskFlagStatus(task: ExternalVendorTask): string {
    return this.flagTextMap[task.task?.flag || ''] || 'Normal Priority';
  }

  getTaskFlagClass(task: ExternalVendorTask): string {
    return `bg-${task.task?.flag || 'gray'}-100 text-${task.task?.flag || 'gray'}-800`;
  }

  getCommentUserName(comment: any): string {
    if (!comment) return 'Unknown User';
    
    const userId = typeof comment.user === 'string' ? comment.user : comment.user?._id;
    if (!userId) return 'Unknown User';

    if (this.userCache[userId]) {
      return this.userCache[userId].name || 'Unknown User';
    }

    this.userService.getUserById(userId).subscribe({
      next: (user) => {
        if (user) {
          this.userCache[userId] = user;
          this.cdr.detectChanges();
        }
      }
    });

    return 'Loading...';
  }

  getCommentUserInitial(comment: any): string {
    const name = this.getCommentUserName(comment);
    return name !== 'Loading...' && name !== 'Unknown User' ? name.charAt(0).toUpperCase() : '?';
  }

  selectTask(task: ExternalVendorTask | null) {
    if (task && task.isRead === false) {
      this.vendorsService.markVendorTaskAsRead(task._id).subscribe({
        next: () => {
          const currentTasks = this.vendorTasksSubject.getValue();
          const updatedTasks = currentTasks.map(t => 
            t._id === task._id ? { ...t, isRead: true } : t
          );
          this.vendorTasksSubject.next(updatedTasks);
          this.unreadCount = Math.max(0, this.unreadCount - 1);
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error marking task as read:', error);
          this.cdr.detectChanges();
        }
      });
    }
    this.selectedTask = task;
    this.cdr.detectChanges();
  }

  isTaskUnread(task: ExternalVendorTask): boolean {
    return task.isRead === false;
  }

  getUnreadCount(): number {
    return this.unreadCount;
  }

  acknowledgeCompletion(task: ExternalVendorTask) {
    if (!task || task.taskCompleted) return;
    this.taskToComplete = task;
    this.isCompleteConfirmOpen = true;
    this.cdr.markForCheck();
  }

  confirmCompleteTask() {
    if (!this.taskToComplete) return;
    this.vendorsService.completeVendorTask(this.taskToComplete._id!).subscribe({
      next: () => {
        this.sysNotifyService.showSuccess('Task completed successfully');
        this.loadVendorTasks();
        this.loadTasksWithSublets();
        this.isCompleteConfirmOpen = false;
        this.taskToComplete = null;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.sysNotifyService.showError('Failed to complete request');
        this.isCompleteConfirmOpen = false;
        this.taskToComplete = null;
        this.cdr.markForCheck();
      }
    });
  }

  cancelCompleteTask() {
    this.isCompleteConfirmOpen = false;
    this.taskToComplete = null;
    this.cdr.markForCheck();
  }

  calculateTimeToComplete(task: ExternalVendorTask): string {
    if (!task.createdAt) return 'N/A';
    
    const start = new Date(task.createdAt);
    const end = new Date();
    const diff = end.getTime() - start.getTime();
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  }

  private playNotificationSound(): void {
    this.soundService.playSound('notification');
  }

  private updatePollingInterval() {
    this.pollingInterval = this.isWebSocketConnected 
      ? this.POLLING_INTERVAL_WS_CONNECTED 
      : this.POLLING_INTERVAL_WS_DISCONNECTED;
    
    // Restart polling with new interval if it's already running
    if (this.pollingSubscription) {
      this.stopPolling();
      this.startPolling();
    }
  }

  private setupWebSocketListeners() {
    const socket = this.ws.getIo();
    if (!socket) {
      console.warn('WebSocket not available for setup');
      return;
    }

    try {
      // Remove existing listeners to prevent duplicates
      socket.off('vendorUpdate');
      socket.off('error');
      socket.off('connect_error');

      // Add error handlers
      socket.on('error', (error) => {
        console.error('WebSocket error:', error);
        this.sysNotifyService.showError('Connection error. Attempting to reconnect...');
        this.isWebSocketConnected = false;
        this.updatePollingInterval();
        this.cdr.detectChanges();
      });

      socket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error);
        this.sysNotifyService.showError('Connection error. Attempting to reconnect...');
        this.isWebSocketConnected = false;
        this.updatePollingInterval();
        this.cdr.detectChanges();
      });

      // Add new listener for vendor updates
      socket.on('vendorUpdate', (message: { 
        type: string, 
        data: { 
          vendorId: string, 
          updateType: 'create' | 'update' | 'urgency' | 'read' | 'completion',
          timestamp: string 
        } 
      }) => {
        if (message.type === 'updateView') {
          this.handleVendorUpdate(message.data).pipe(
            takeUntil(this.destroy$)
          ).subscribe({
            error: (error) => {
              console.error('Error handling vendor update:', error);
              this.sysNotifyService.showError('Error processing update');
            }
          });
        }
      });
    } catch (error) {
      console.error('Error setting up WebSocket listeners:', error);
      this.sysNotifyService.showError('Error setting up real-time updates');
      this.cdr.detectChanges();
    }
  }

  private handleVendorUpdate(update: { 
    vendorId: string, 
    updateType: 'create' | 'update' | 'urgency' | 'read' | 'completion',
    timestamp: string 
  }) {
    this.lastWebSocketUpdate = new Date(update.timestamp);
    
    // For immediate updates, reload the data
    if (update.updateType === 'create' || update.updateType === 'urgency' || update.updateType === 'completion') {
      return this.loadVendorTasks().pipe(
        switchMap(() => this.loadTasksWithSublets()),
        tap(() => this.cdr.detectChanges()),
        catchError(error => {
          console.error('Error in vendor update:', error);
          this.cdr.detectChanges();
          return EMPTY;
        })
      );
    } else {
      // For less critical updates, just update the specific task
      const currentTasks = this.vendorTasksSubject.getValue();
      const taskIndex = currentTasks.findIndex(task => task._id === update.vendorId);
      
      if (taskIndex !== -1) {
        return this.vendorsService.getVendorById({ _id: update.vendorId }).pipe(
          tap(updatedTask => {
            const updatedTasks = [...currentTasks];
            updatedTasks[taskIndex] = updatedTask;
            this.vendorTasksSubject.next(updatedTasks);
            this.updateTaskMetrics(updatedTasks);
            this.cdr.detectChanges();
          }),
          catchError(error => {
            console.error('Error updating specific task:', error);
            // Fallback to full reload if specific update fails
            return this.loadVendorTasks().pipe(
              tap(() => this.cdr.detectChanges()),
              catchError(fallbackError => {
                console.error('Error in fallback reload:', fallbackError);
                this.cdr.detectChanges();
                return EMPTY;
              })
            );
          })
        );
      }
    }
    return EMPTY;
  }

  openImageViewer(media: Media, index: number, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const images = this.selectedTask?.media?.filter(m => m.imageUrl).map(m => m.imageUrl) || [];
    this.imageViewerService.open(images, index);
  }

  closeImageViewer() {
    if (this.imageViewer) {
      this.imageViewer.close();
      document.body.style.overflow = 'auto';
    }
    this.selectedImageIndex = null;
    this.cdr.detectChanges();
  }

  getImagesForViewer(): (string | { mediaId: { _id: string; imageUrl: string; name?: string }; rotation: number })[] {
    if (!this.selectedTask?.media) return [];
    return this.selectedTask.media.map(media => ({
      mediaId: {
        _id: media._id || '', // Use the media's _id or empty string if not available
        imageUrl: media.imageUrl,
        name: media.caption
      },
      rotation: 0 // Default rotation, can be updated if we have rotation data
    }));
  }
} 